import { CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Select, Tag, Tooltip, Typography } from "antd";
import mergeClassNames from "common/mergeClassNames";
import AiMappingConfidenceBadge from "components/atoms/AiMappingConfidenceBadge";
import React from "react";

const { Text } = Typography;
const { Option } = Select;

interface AiMappingRowProps {
    sourceColumn: string;
    targetAttribute: string;
    confidence: number;
    reasoning: string;
    sourceColumnExists: boolean;
    targetAttributeExists: boolean;
    sourceDataType?: string;
    targetDataType?: string;
    targetDescription?: string;
    onTargetChange?: (value: string) => void;
    targetOptions?: Array<{ label: string; value: string }>;
    className?: string;
}

const AiMappingRow: React.FC<AiMappingRowProps> = ({
    sourceColumn,
    targetAttribute,
    confidence,
    reasoning,
    sourceColumnExists,
    targetAttributeExists,
    sourceDataType = "String",
    targetDataType,
    targetDescription,
    onTargetChange,
    targetOptions = [],
    className = "",
}) => {
    const getStatusIcon = (exists: boolean) => {
        return exists ? <CheckCircleOutlined className="text-lg text-green-600" /> : <CloseCircleOutlined className="text-lg text-red-600" />;
    };

    const getTargetDisplayText = () => {
        let displayText = targetAttribute;
        if (targetDataType) {
            displayText += ` (${targetDataType})`;
        }
        if (targetDescription) {
            displayText += ` (${targetDescription})`;
        }
        return displayText;
    };

    return (
        <div className={mergeClassNames("ai-mapping-row flex items-center gap-4 border-b border-gray-100 p-4 hover:bg-gray-50", className)}>
            {/* Source Field Column */}
            <div className="flex min-w-0 flex-1 items-center gap-3">
                {getStatusIcon(sourceColumnExists)}
                <div className="min-w-0 flex-1">
                    <Text strong className="block truncate text-gray-900">
                        {sourceColumn}
                    </Text>
                    <Tag className="border-gray-200 bg-gray-100 text-gray-600">{sourceDataType}</Tag>
                </div>
            </div>

            {/* Target Field Column */}
            <div className="flex min-w-0 flex-1 items-center gap-3">
                <AiMappingConfidenceBadge confidence={confidence} />

                {onTargetChange && targetOptions.length > 0 ? (
                    <Select value={targetAttribute} onChange={onTargetChange} className="min-w-48" size="small">
                        {targetOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                ) : (
                    <Text className="min-w-48 text-gray-700">{targetAttribute}</Text>
                )}
            </div>

            {/* Mapped To Column */}
            <div className="flex min-w-0 flex-1 items-center gap-2">
                <Text className="text-gray-600">Mapped to:</Text>
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <Text className="truncate text-gray-900">{getTargetDisplayText()}</Text>
                    <Tooltip title={reasoning} placement="top">
                        <InfoCircleOutlined className="cursor-help text-sm text-gray-400" />
                    </Tooltip>
                </div>
            </div>
        </div>
    );
};

export default AiMappingRow;
